// Notification Service
import { getFunctions, httpsCallable } from 'firebase/functions';

class NotificationService {
  constructor() {
    this.functions = getFunctions();
    
    // Initialize callable functions
    this.sendCustomNotification = httpsCallable(this.functions, 'sendCustomNotification');
  }

  /**
   * Send a custom notification email (Admin only)
   * @param {string} userEmail - Recipient email address
   * @param {string} userName - Recipient name (optional)
   * @param {string} subject - Email subject
   * @param {string} message - Email message content
   * @returns {Promise<Object>} Result of the operation
   */
  async sendCustomNotification(userEmail, userName, subject, message) {
    try {
      const result = await this.sendCustomNotification({
        userEmail,
        userName,
        subject,
        message
      });
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('Error sending custom notification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Show in-app notification
   * @param {string} type - Notification type (success, error, warning, info)
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {number} duration - Auto-close duration in milliseconds (0 for manual close)
   */
  showNotification(type = 'info', title, message, duration = 5000) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <div class="notification-header">
          <span class="notification-icon">${this.getIcon(type)}</span>
          <span class="notification-title">${title}</span>
          <button class="notification-close" onclick="this.parentElement.parentElement.parentElement.remove()">×</button>
        </div>
        ${message ? `<div class="notification-message">${message}</div>` : ''}
      </div>
    `;

    // Add styles if not already present
    if (!document.getElementById('notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'notification-styles';
      styles.textContent = `
        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          min-width: 300px;
          max-width: 500px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          animation: slideIn 0.3s ease-out;
          border-left: 4px solid;
        }
        
        .notification-success { border-left-color: #28a745; }
        .notification-error { border-left-color: #dc3545; }
        .notification-warning { border-left-color: #ffc107; }
        .notification-info { border-left-color: #17a2b8; }
        
        .notification-content {
          padding: 16px;
        }
        
        .notification-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        
        .notification-icon {
          font-size: 18px;
        }
        
        .notification-title {
          font-weight: 600;
          color: #333;
          flex: 1;
        }
        
        .notification-close {
          background: none;
          border: none;
          font-size: 20px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .notification-close:hover {
          color: #666;
        }
        
        .notification-message {
          color: #666;
          font-size: 14px;
          line-height: 1.4;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    // Add to DOM
    document.body.appendChild(notification);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }, duration);
    }

    return notification;
  }

  /**
   * Get icon for notification type
   * @param {string} type - Notification type
   * @returns {string} Icon HTML
   */
  getIcon(type) {
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };
    return icons[type] || icons.info;
  }

  /**
   * Show success notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {number} duration - Auto-close duration
   */
  success(title, message, duration = 5000) {
    return this.showNotification('success', title, message, duration);
  }

  /**
   * Show error notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {number} duration - Auto-close duration (0 for manual close)
   */
  error(title, message, duration = 0) {
    return this.showNotification('error', title, message, duration);
  }

  /**
   * Show warning notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {number} duration - Auto-close duration
   */
  warning(title, message, duration = 7000) {
    return this.showNotification('warning', title, message, duration);
  }

  /**
   * Show info notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {number} duration - Auto-close duration
   */
  info(title, message, duration = 5000) {
    return this.showNotification('info', title, message, duration);
  }

  /**
   * Show KYC status notification
   * @param {string} status - KYC status (approved, rejected, pending)
   * @param {string} notes - Additional notes
   */
  showKYCStatusNotification(status, notes = '') {
    switch (status) {
      case 'approved':
        this.success(
          'KYC Approved! 🎉',
          'Your identity verification has been approved. You can now request payouts and access all features.',
          8000
        );
        break;
      case 'rejected':
        this.error(
          'KYC Review Required',
          `Your KYC submission needs additional review. ${notes ? `Reason: ${notes}` : 'Please check your documents and resubmit.'}`,
          0
        );
        break;
      case 'pending':
        this.info(
          'KYC Under Review',
          'Your KYC documents are being reviewed. You\'ll receive an email update within 24-48 hours.',
          6000
        );
        break;
      default:
        this.info('KYC Status Updated', `Your KYC status has been updated to: ${status}`);
    }
  }

  /**
   * Show payout status notification
   * @param {string} status - Payout status
   * @param {number} amount - Payout amount
   * @param {string} notes - Additional notes
   */
  showPayoutStatusNotification(status, amount = 0, notes = '') {
    const amountText = amount > 0 ? `₹${amount}` : '';
    
    switch (status) {
      case 'approved':
        this.success(
          'Payout Approved! 💰',
          `Your payout request for ${amountText} has been approved and is being processed. Funds will arrive in 1-2 business days.`,
          8000
        );
        break;
      case 'completed':
        this.success(
          'Payout Completed! ✅',
          `Your payout of ${amountText} has been successfully transferred to your bank account. Please check your account.`,
          10000
        );
        break;
      case 'failed':
      case 'rejected':
        this.error(
          'Payout Issue',
          `Your payout request for ${amountText} could not be processed. ${notes ? `Reason: ${notes}` : ''} Your funds have been returned to your wallet.`,
          0
        );
        break;
      case 'pending':
        this.info(
          'Payout Submitted',
          `Your payout request for ${amountText} has been submitted for review. You'll receive an update soon.`,
          6000
        );
        break;
      default:
        this.info('Payout Status Updated', `Your payout status has been updated to: ${status}`);
    }
  }

  /**
   * Show balance update notification
   * @param {number} amount - Amount added or deducted
   * @param {string} reason - Reason for balance change
   */
  showBalanceUpdateNotification(amount, reason = '') {
    if (amount > 0) {
      this.success(
        'Balance Updated! 💰',
        `₹${amount} has been added to your wallet. ${reason ? `Reason: ${reason}` : ''}`,
        6000
      );
    } else if (amount < 0) {
      this.warning(
        'Balance Deducted',
        `₹${Math.abs(amount)} has been deducted from your wallet. ${reason ? `Reason: ${reason}` : ''}`,
        6000
      );
    }
  }

  /**
   * Clear all notifications
   */
  clearAll() {
    const notifications = document.querySelectorAll('.notification');
    notifications.forEach(notification => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    });
  }

  /**
   * Convenience methods for simple message notifications
   */
  showSuccess(message) {
    return this.success('Success', message);
  }

  showError(message) {
    return this.error('Error', message);
  }

  showWarning(message) {
    return this.warning('Warning', message);
  }

  showInfo(message) {
    return this.info('Info', message);
  }
}

// Create and export singleton instance
const notificationService = new NotificationService();
export { notificationService };
export default notificationService;